import { environment } from '®environments/environment';

import { AppConfig, InvalidConfigException } from './app-config';
import { AppConfigFromWindowContext } from './app-config-from-window-context';
import { AppConfigLocalDev } from './app-config-local-dev';

export class AppConfigurator {
  /**
   * Get the AppConfig being used by the application, for injection.
   *
   * NOTE: this attempts to load the config from the `window` global; if this is missing,
   * a hard-coded value is used; if it is present but invalid, an exception is thrown.
   *
   * @returns {AppConfig} - The AppConfig to use across the app.
   **/
  getAppConfig(
    isInProduction = environment.production,
    windowWithConfig: any = window,
  ): AppConfig {
    // when not in production, use local dev config
    if (!isInProduction) {
      return new AppConfigLocalDev();
    }

    // when in production, validate window configuration
    const config = this.getConfigFromWindow(windowWithConfig);
    if (!this.isValidConfig(config)) {
      throw new InvalidConfigException(config, 'window.shipshape.config');
    }

    // convert to an AppConfig subtype, and revalidate to ensure properties are transferred
    const appConfig = new AppConfigFromWindowContext(config);
    if (!this.isValidConfig(appConfig)) {
      throw new InvalidConfigException(appConfig, 'AppConfigFromWindowContext');
    }

    return appConfig;
  }

  /**
   * Gets the configuration for Shipshape from `window.shipshape.config`, if available.
   *
   * @param {any} windowAsAny - the `window` object, as an `any`-typed JSON object.
   * @returns {Class<AppConfig>} - The class of the AppConfig to use.
   **/
  getConfigFromWindow(windowAsAny: any): any {
    const windowConfig =
      windowAsAny && windowAsAny.shipshape && windowAsAny.shipshape.config;
    return windowConfig ? (windowConfig as AppConfig) : undefined;
  }

  /**
   * Validate a JSON object contains a valid configuration.
   *
   * @param {any} config - any JSON object that might contain AppConfig data
   * @returns {boolean} - `true` when the config is valid; `false` if invalid.
   */
  isValidConfig(config: any): boolean {
    // deconstruct the main subconfig
    const {
      apiEndpoint,
      personaEndpoint,
      talisLoginProvider,
      tenant = undefined,
    } = config;

    // deconstruct the tenant subconfig
    const {
      conceptBaseUri = undefined,
      hierarchy = undefined,
      httpsBaseUri = undefined,
      loginProvider = undefined,
      name = undefined,
      roleMapping = undefined,
      roleCodesByRoleId = undefined,
      shortCode = undefined,
    } = tenant || {};

    // verify all config values are specified.
    const mandatoryConfigValues = [
      apiEndpoint,
      personaEndpoint,
      talisLoginProvider,
      tenant,
      conceptBaseUri,
      hierarchy,
      httpsBaseUri,
      loginProvider,
      name,
      roleMapping,
      roleCodesByRoleId,
      shortCode,
    ];

    return mandatoryConfigValues.every(value => value && true);
  }
}

export const appConfigurator: AppConfigurator = new AppConfigurator();

export function configureApp(): AppConfig {
  return appConfigurator.getAppConfig();
}
